import React, { useEffect, useState } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import Layout from "./components/Layout";

import ProtectedRoute from "./components/ProtectedRoutes";
import Home from "./Pages/Home";
import About from "./Pages/About";
import Doctors from "./Pages/Doctors";
import Blog from "./Pages/Blog";
import Departments from "./Pages/Departments";
import DepartmentDetails from "./Pages/DepartmentDetails";
import BlogDetails from "./Pages/BlogDetails";
import DoctorDetails from "./Pages/DoctorDetails";
import PricingPlan from "./Pages/PricingPlan";
import Gallery from "./Pages/Gallery";
import Timetable from "./Pages/Timetable";
import Contact from "./Pages/Contact";
import ErrorPage from "./Pages/ErrorPage";
import AppointmentDateSelectPage from "./Pages/AppointmentDateSelect/AppointmentDateSelect";
import AppointmentSummary from "./Pages/AppointmentSummary/AppointmentSummary";
import Profile from "./Pages/Profile/Profile";
import UserWallet from "./Pages/UserWallet/UserWallet";
import PatientAppointments from "./Pages/PatientAppointments/PatientAppointments";
import AppointmentDetails from "./Pages/AppointmentDetails/AppointmentDetails";
import ManagePatients from "./Pages/ManagePatients/ManagePatients";
import DoctorDashboard from "./Pages/DoctorDashboard/DoctorDashboard";
import Login from "./Pages/Auth/Login";
import Register from "./Pages/Auth/Register";
import UserProfileSetup from "./Pages/UserProfileSetup/UserProfileSetup";
import axiosInstance from "./utils/axiosInstance";
import { fetchDoctorProfile, fetchGlobalSettings, fetchMyUserDetails } from "./constants/ApiConstants";
import { updateDoctorData, updateSettings, updateUserData } from "./store/appSlice";
import EditProfile from "./Pages/Profile/EditProfile";
import LoginAsDoctor from "./Pages/Auth/LoginAsDoctor";
import DoctorProfileSetup from "./Pages/DoctorProfileSetup/DoctorProfileSetup";
import DoctorProfileSetupOne from "./Pages/DoctorProfileSetup/DoctorProfileSetupOne";
import DoctorProfileSetupTwo from "./Pages/DoctorProfileSetup/DoctorProfileSetupTwo";
import DoctorProfileSetupThree from "./Pages/DoctorProfileSetup/DoctorProfileSetupThree";
import DoctorRegistrationSuccess from "./Pages/DoctorProfileSetup/DoctorRegistrationSuccess";
import SelectCategory from "./Pages/Categories/SelectCategory";
import AppointmentBooked from "./Pages/AppointmentBooked/AppointmentBooked";
import DoctorProfile from "./Pages/DoctorProfile/DoctorProfile";
import ManageServices from "./Pages/ManageServices/ManageServices";
import DoctorSetting from "./Pages/DoctorSetting/DoctorSetting";
import EditDoctorProfile from "./Pages/EditDoctorProfile/EditDoctorProfile";
import EditDoctorPersonalInformation from "./Pages/EditDoctorProfile/comps/EditDoctorPersonalInformation";
import DoctorCategories from "./Pages/EditDoctorProfile/comps/DoctorCategories";
import EditAboutYourselfEducation from "./Pages/EditDoctorProfile/comps/EditAboutYourselfEducation";
import EditConsultationType from "./Pages/EditDoctorProfile/comps/EditConsultationType";
import ManageExpertise from "./Pages/EditDoctorProfile/comps/ManageExpertise";
import ManageExperience from "./Pages/EditDoctorProfile/comps/ManageExperience";
import ManageAwards from "./Pages/EditDoctorProfile/comps/ManageAwards";
import ManageServiceLocations from "./Pages/EditDoctorProfile/comps/ManageServiceLocations";
import DoctorAppointmentSlots from "./Pages/DoctorAppointmentSlots/DoctorAppointmentSlots";
import ManageHolidays from "./Pages/ManageHolidays/ManageHolidays";
import DoctorWallet from "./Pages/DoctorWallet/DoctorWallet";
import BankDetails from "./Pages/BankDetails/BankDetails";
import RegisterAsDoctor from "./Pages/Auth/RegisterAsDoctor";
import ProtectedDoctorRoute from "./components/ProtectedDoctorRoute";
import AppointmentRequests from "./Pages/AppointmentRequests/AppointmentRequests";
import AddPrescriptions from "./Pages/AddPrescriptions/AddPrescriptions";
import VideoChat from "./Pages/VideoChat/VideoChatUser";

function App() {
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true); // Track loading state

  useEffect(() => {
    const fetchData = async () => {
      try {
        const globalSettingsResponse = await axiosInstance.post(`https://vdoc.nexilo.in/api${fetchGlobalSettings}`);
        dispatch(updateSettings(globalSettingsResponse.data.data));
        const savedUserData = JSON.parse(localStorage.getItem("userData"));
        if (savedUserData.userId && savedUserData.role) {
          if (savedUserData?.role === "USER") {
            const userDetailsResponse = await axiosInstance.post(`https://vdoc.nexilo.in/api${fetchMyUserDetails}`, {
              user_id: savedUserData?.userId,
            });
            dispatch(updateUserData(userDetailsResponse?.data.data));
          } else if (savedUserData?.role === "DOCTOR") {
            const doctorDetailResponse = await axiosInstance.post(`https://vdoc.nexilo.in/api${fetchDoctorProfile}`, {
              doctor_id: savedUserData?.userId,
            });
            dispatch(updateDoctorData(doctorDetailResponse?.data.data));
          }
        }
      } catch (error) {
        console.error("Error fetching initial data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [dispatch]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  if (loading) {
    return null;
  }

  return (
    <Routes>
      <Route path="/" element={<Layout />}>
        <Route index element={<Home />} />
        <Route path="about" element={<About />} />
        <Route path="doctors" element={<Doctors />} />
        <Route path="doctors/:doctorId" element={<DoctorDetails />} />
        <Route path="blog" element={<Blog />} />
        <Route path="blog/:blogId" element={<BlogDetails />} />
        <Route path="departments" element={<Departments />} />
        <Route path="search" element={<DepartmentDetails />} />
        <Route path="pricing-plan" element={<PricingPlan />} />
        <Route path="gallery" element={<Gallery />} />
        <Route path="timetable" element={<Timetable />} />
        <Route path="contact" element={<Contact />} />

        {/*  Auth routes */}
        <Route path="login" element={<Login />} />
        <Route path="register" element={<Register />} />
        <Route path="login-as-doctor" element={<LoginAsDoctor />} />
        <Route path="doctor-register" element={<RegisterAsDoctor />} />
        {/* Auth Routes Ends Here */}

        {/*  Doctor Protected Routes */}
        <Route path="complete-doctor-profile" element={<DoctorProfileSetup />} />
        <Route path="select-category" element={<SelectCategory />} />
        <Route
          path="appointment-requests"
          element={
            <ProtectedDoctorRoute>
              <AppointmentRequests />
            </ProtectedDoctorRoute>
          }
        />
        <Route
          path="add-prescriptions/:appointmentId"
          element={
            <ProtectedDoctorRoute>
              <AddPrescriptions />
            </ProtectedDoctorRoute>
          }
        />
        <Route
          path="doctor-settings"
          element={
            <ProtectedDoctorRoute>
              <DoctorSetting />
            </ProtectedDoctorRoute>
          }
        />
        <Route
          path="/doctor-settings/edit-doctor-profile"
          element={
            <ProtectedDoctorRoute>
              <EditDoctorProfile />
            </ProtectedDoctorRoute>
          }
        />
        <Route
          path="/doctor-settings/edit-doctor-profile/personal-information"
          element={
            <ProtectedDoctorRoute>
              <EditDoctorPersonalInformation />
            </ProtectedDoctorRoute>
          }
        />
        <Route
          path="/doctor-settings/edit-doctor-profile/categories"
          element={
            <ProtectedDoctorRoute>
              <DoctorCategories />
            </ProtectedDoctorRoute>
          }
        />
        <Route
          path="/doctor-settings/edit-doctor-profile/about-yourself-education"
          element={
            <ProtectedDoctorRoute>
              <EditAboutYourselfEducation />
            </ProtectedDoctorRoute>
          }
        />
        <Route
          path="/doctor-settings/edit-doctor-profile/consultation-type"
          element={
            <ProtectedDoctorRoute>
              <EditConsultationType />
            </ProtectedDoctorRoute>
          }
        />
        <Route
          path="/doctor-settings/edit-doctor-profile/manage-services"
          element={
            <ProtectedDoctorRoute>
              <ManageServices />
            </ProtectedDoctorRoute>
          }
        />
        <Route
          path="/doctor-settings/edit-doctor-profile/manage-expertises"
          element={
            <ProtectedDoctorRoute>
              <ManageExpertise />
            </ProtectedDoctorRoute>
          }
        />
        <Route
          path="/doctor-settings/edit-doctor-profile/manage-experiences"
          element={
            <ProtectedDoctorRoute>
              <ManageExperience />
            </ProtectedDoctorRoute>
          }
        />
        <Route
          path="/doctor-settings/edit-doctor-profile/manage-awards"
          element={
            <ProtectedDoctorRoute>
              <ManageAwards />
            </ProtectedDoctorRoute>
          }
        />
        <Route
          path="/doctor-settings/edit-doctor-profile/manage-service-locations"
          element={
            <ProtectedDoctorRoute>
              <ManageServiceLocations />
            </ProtectedDoctorRoute>
          }
        />
        <Route
          path="/doctor-settings/appointment-slots"
          element={
            <ProtectedDoctorRoute>
              <DoctorAppointmentSlots />
            </ProtectedDoctorRoute>
          }
        />
        <Route
          path="/doctor-settings/manage-holidays"
          element={
            <ProtectedDoctorRoute>
              <ManageHolidays />
            </ProtectedDoctorRoute>
          }
        />
        <Route
          path="/doctor-settings/wallet"
          element={
            <ProtectedDoctorRoute>
              <DoctorWallet />
            </ProtectedDoctorRoute>
          }
        />
        <Route
          path="/doctor-settings/bank-details"
          element={
            <ProtectedDoctorRoute>
              <BankDetails />
            </ProtectedDoctorRoute>
          }
        />
        <Route
          path="/doctor-dashboard"
          element={
            <ProtectedDoctorRoute>
              <DoctorDashboard />
            </ProtectedDoctorRoute>
          }
        />
        <Route
          path="doctor-profile"
          element={
            <ProtectedDoctorRoute>
              <DoctorProfile />
            </ProtectedDoctorRoute>
          }
        />
        <Route
          path="manage-services"
          element={
            <ProtectedDoctorRoute>
              <ManageServices />
            </ProtectedDoctorRoute>
          }
        />

        <Route path="doctor-profile-setup/one" element={<DoctorProfileSetupOne />} />
        <Route path="doctor-profile-setup/two" element={<DoctorProfileSetupTwo />} />
        <Route path="doctor-profile-setup/three" element={<DoctorProfileSetupThree />} />
        <Route path="doctor-registration-success" element={<DoctorRegistrationSuccess />} />
        {/*  Doctor Protected Routes Ends Here*/}

        {/* User Proteced Routes */}
        <Route path="complete-profile" element={<UserProfileSetup />} />
        <Route path="date-select/:doctorId" element={<AppointmentDateSelectPage />} />
        <Route path="appointment-summary" element={<AppointmentSummary />} />
        <Route path="appointment-details/:appointmentId" element={<AppointmentDetails />} />
        <Route path="appointment-booked/:appointmentId" element={<AppointmentBooked />} />
        <Route
          path="profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route
          path="edit-profile"
          element={
            <ProtectedRoute>
              <EditProfile />
            </ProtectedRoute>
          }
        />
        <Route
          path="user-wallet"
          element={
            <ProtectedRoute>
              <UserWallet />
            </ProtectedRoute>
          }
        />
        <Route
          path="patient-appointments"
          element={
            <ProtectedRoute>
              <PatientAppointments />
            </ProtectedRoute>
          }
        />
        <Route
          path="manage-patients"
          element={
            <ProtectedRoute>
              <ManagePatients />
            </ProtectedRoute>
          }
        />
      </Route>
      <Route path="start-video-call" element={<VideoChat />} />
      {/* User Protected Routes Ends Here */}

      {/* <Route element={<Layout2 />}>
        <Route path="home-v2" element={<HomeStyle2 />} />
        <Route path="home-v6" element={<HomeStyle6 />} />
      </Route>
      <Route path="home-v3" element={<Layout3 />}>
        <Route index element={<HomeStyle3 />} />
      </Route>
      <Route path="home-v4" element={<Layout4 />}>
        <Route index element={<HomeStyle4 />} />
      </Route>
      <Route path="home-v5" element={<Layout5 />}>
        <Route index element={<HomeStyle5 />} />
      </Route> */}
      <Route path="*" element={<ErrorPage />} />
      <Route path="/page-not-found" element={<ErrorPage />} />
    </Routes>
  );
}

export default App;
